import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import api from '../lib/api';
import { conversationKeys } from './conversations';

export enum PleriTaskStatus {
  Active = 'active',
  Archived = 'archived',
  Deleted = 'deleted',
  Paused = 'paused',
}
// Types
export interface Task {
  id: string;
  tenantId: string;
  organizationId: string;
  name: string;
  scope: string;
  ownerId: string;
  description: string;
  workspaceIds: string[];
  notificationWorkspaceIds: string[];
  schedule?: RecurringSchedule | OneTimeSchedule;
  status: PleriTaskStatus;
  meta?: {
    userConfigError?: {
      occurredAt: string;
      errorMessage: string;
      errorCode: string;
    };
  };
  taskType?: 'CODE_FIX';
  configurations?: {
    excludeRepoDueToInactivity?: Array<{ id: string; link: string }>;
  };
  createdAt: string;
  lastUpdated: string;
  dataTestId?: string;
}

export interface RecurringSchedule {
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  time: string; // HH:mm format
  timezone: string;
}

export interface OneTimeSchedule {
  runAt: string; // ISO 8601 date-time
}

export interface CreateTaskRequest {
  name: string;
  description: string;
  goal: string;
  schedule?: RecurringSchedule | OneTimeSchedule;
}

export interface UpdateTaskRequest {
  name?: string;
  description?: string;
  goal?: string;
  workspaceIds?: string[];
  notificationWorkspaceIds?: string[];
  schedule?: RecurringSchedule | OneTimeSchedule | null;
  config?: {
    timeRange?: {
      unit: 'days' | 'hours';
      value: number;
    };
    checks: string[];
    parameters?: Record<string, unknown>;
  };
  status?: PleriTaskStatus;
}

// API paths

// Query keys
export const taskKeys = {
  all: ['tasks'] as const,
  lists: (params?: { scope?: string; status?: string }) =>
    [...taskKeys.all, 'list', params] as const,
  detail: (scope: string, id: string) =>
    [...taskKeys.all, 'detail', scope, id] as const,
};

// Hooks
export const useListTasks = (
  params: { scope?: string; status?: string } = {},
  options?: Partial<UseQueryOptions<Task[], Error>>,
) => {
  return useQuery({
    queryKey: taskKeys.lists(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.scope) {
        queryParams.append('scope', params.scope);
      }
      if (params.status) {
        queryParams.append('status', params.status);
      }
      const queryString = queryParams.toString();
      const url = queryString
        ? `/api/core/pleri/tasks?${queryString}`
        : `/api/core/pleri/tasks`;
      const { data } = await api.get<{ data: Task[] }>(url);
      return data.data;
    },
    ...options,
  });
};

export const useGetTask = (scope: string, taskId: string) => {
  return useQuery({
    queryKey: taskKeys.detail(scope, taskId),
    queryFn: async () => {
      const { data } = await api.get<{ data: Task }>(
        `/api/core/pleri/tasks/${scope}/${taskId}`,
      );
      return data.data;
    },
    enabled: !!scope && !!taskId,
  });
};

export const useCreateTask = (scope: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateTaskRequest) => {
      const { data } = await api.post<{ data: Task }>(
        `/api/core/pleri/tasks/${scope}`,
        request,
      );
      await queryClient.refetchQueries({ queryKey: taskKeys.lists({ scope }) });
      return data.data;
    },
  });
};

export const useUpdateTask = (scope: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      request,
    }: {
      taskId: string;
      request: UpdateTaskRequest;
    }) => {
      const { data } = await api.put<{ data: Task }>(
        `/api/core/pleri/tasks/${scope}/${taskId}`,
        request,
      );
      await Promise.all([
        queryClient.refetchQueries({ queryKey: taskKeys.lists({ scope }) }),
        queryClient.refetchQueries({
          queryKey: taskKeys.detail(scope, taskId),
        }),
      ]);
      return data.data;
    },
  });
};

export const useDeleteTask = (scope: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      await api.delete(`/api/core/pleri/tasks/${scope}/${taskId}`);
      await Promise.all([
        queryClient.refetchQueries({ queryKey: taskKeys.lists({ scope }) }),
        queryClient.refetchQueries({ queryKey: conversationKeys.all }),
      ]);
    },
  });
};
